import React, { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateRangeSelector from 'legacyfrontend/components/filters/DateRangeSelector';
import { DestinationAutocomplete } from './DestinationAutocomplete';
import { HotelPassengerSelector } from './HotelPassengerSelector';
import { getMissingHotelFieldLabels, validateHotelSearch } from '../utils/validateHotelSearch';
import type { HotelSearchFormErrors, HotelSearchFormValues } from '../types/hotels.presentation';
import type { DestinationSuggestion } from '../api/hotels.types';

interface HotelSearchFormProps {
  onSubmit: (query: HotelSearchFormValues) => void;
  isSubmitting: boolean;
  formRef?: React.RefObject<HTMLFormElement>;
  initialValues?: Partial<HotelSearchFormValues>;
}

interface DateRangeSelectorProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (range: { startDate?: Date; endDate?: Date | null }) => void;
  error?: boolean;
  helperText?: string;
  minDate?: Date;
  startDateLabel?: string;
  endDateLabel?: string;
}

const FlightDateRangeSelector =
  DateRangeSelector as unknown as React.ComponentType<DateRangeSelectorProps>;

const defaultValues: HotelSearchFormValues = {
  destination: '',
  destinationId: undefined,
  countryCode: undefined,
  checkIn: '',
  checkOut: '',
  adults: 1,
  children: 0,
  rooms: 1,
};

const INPUT_HEIGHT = 48;

const buildInitialValues = (
  initialValues?: Partial<HotelSearchFormValues>,
): HotelSearchFormValues => ({
  ...defaultValues,
  ...initialValues,
  adults: initialValues?.adults ?? defaultValues.adults,
  children: initialValues?.children ?? defaultValues.children,
  childrenAges: Array.from(
    { length: initialValues?.children ?? defaultValues.children },
    (_, i) => initialValues?.childrenAges?.[i],
  ),
  rooms: initialValues?.rooms ?? defaultValues.rooms,
});

const FIELD_LABEL_STYLE: React.CSSProperties = {
  color: '#4f4f4f',
  fontSize: 12,
  lineHeight: '18px',
  minHeight: '18px',
};

const buildMissingFieldsMessage = (labels: string[]) => {
  if (labels.length === 1) {
    return `Completá ${labels[0]} para continuar.`;
  }

  const last = labels[labels.length - 1];
  const previousLabels = labels.slice(0, -1).join(', ');

  return `Completá ${previousLabels} y ${last} para continuar.`;
};

const formatDateLocal = (date: Date | null | undefined) => {
  if (!date || Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value: string) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDestinationLabel = (destination: DestinationSuggestion) =>
  `${destination.name}, ${destination.countryName ?? destination.countryCode}`;

export function HotelSearchForm({
  onSubmit,
  isSubmitting,
  formRef,
  initialValues,
}: HotelSearchFormProps) {
  const { t } = useTranslation();
  const hydratedInitialValues = useMemo(() => buildInitialValues(initialValues), [initialValues]);
  const [values, setValues] = useState<HotelSearchFormValues>(hydratedInitialValues);
  const [errors, setErrors] = useState<HotelSearchFormErrors>({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [selectedDestination, setSelectedDestination] = useState<DestinationSuggestion | null>(
    null,
  );
  const fieldRefs = useRef<Partial<Record<keyof HotelSearchFormValues, HTMLElement | null>>>({
    destination: null,
    checkIn: null,
    checkOut: null,
    adults: null,
    children: null,
    rooms: null,
  });

  useEffect(() => {
    setValues(hydratedInitialValues);
    setSelectedDestination(null);
    setErrors({});
    setSubmitMessage('');
  }, [hydratedInitialValues]);

  const updateField = (field: 'destination' | 'checkIn' | 'checkOut', rawValue: string) => {
    setValues((current) => ({
      ...current,
      [field]: rawValue,
      ...(field === 'destination' ? { countryCode: undefined, destinationId: undefined } : {}),
    }));

    if (field === 'destination') {
      setSelectedDestination(null);
    }
  };

  const handleSelectDestination = (destination: DestinationSuggestion) => {
    setSelectedDestination(destination);
    setValues((current) => ({
      ...current,
      destination: getDestinationLabel(destination),
      destinationId: destination.id,
      countryCode: destination.countryCode,
    }));
  };

  const updateDateRange = ({ startDate, endDate }: { startDate?: Date; endDate?: Date | null }) => {
    setValues((current) => ({
      ...current,
      checkIn: formatDateLocal(startDate),
      checkOut: formatDateLocal(endDate),
    }));
  };

  const updateGuests = (
    guests: Pick<HotelSearchFormValues, 'adults' | 'children' | 'childrenAges' | 'rooms'>,
  ) => {
    const children = guests.children ?? 0;
    // One age slot per child: keep the ages already chosen, drop/pad the rest.
    const childrenAges = Array.from({ length: children }, (_, i) => guests.childrenAges?.[i]);
    setValues((current) => ({ ...current, ...guests, childrenAges }));
  };

  const focusFirstInvalidField = (nextErrors: HotelSearchFormErrors) => {
    const firstInvalidField = (
      ['destination', 'checkIn', 'checkOut', 'adults', 'children', 'rooms'] as const
    ).find((field) => nextErrors[field]);

    if (firstInvalidField) {
      window.setTimeout(() => fieldRefs.current[firstInvalidField]?.focus(), 0);
    }
  };

  const submitCurrentValues = () => {
    const nextValues: HotelSearchFormValues = {
      ...values,
      destination: selectedDestination
        ? getDestinationLabel(selectedDestination)
        : values.destination.trim(),
      destinationId: selectedDestination?.id ?? values.destinationId,
      countryCode: selectedDestination?.countryCode ?? values.countryCode,
      childrenAges: (values.children ?? 0) > 0 ? values.childrenAges : undefined,
    };
    const result = validateHotelSearch(nextValues);
    const missingLabels = getMissingHotelFieldLabels(nextValues);
    setErrors(result.errors);

    if (!result.isValid) {
      const firstError = Object.values(result.errors)[0];
      setSubmitMessage(
        missingLabels.length > 0
          ? buildMissingFieldsMessage(missingLabels)
          : t(firstError?.message ?? ''),
      );
      focusFirstInvalidField(result.errors);
      return;
    }

    setSubmitMessage('');
    onSubmit(nextValues);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitCurrentValues();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) {
      return;
    }

    event.preventDefault();
    event.currentTarget.requestSubmit();
  };

  const helperRow = (field: keyof HotelSearchFormValues, testId: string) => {
    const error = errors[field];

    if (!error) return null;

    return (
      <p
        className='absolute left-0 top-full mt-0.5 text-[11px] leading-tight text-red-600'
        data-testid={testId}
        id={`${field}-error`}
      >
        {t(error.message)}
      </p>
    );
  };

  return (
    <form
      aria-label={t('hotels.form.searchFormLabel')}
      className='package-search__form__fields'
      noValidate
      onKeyDown={handleKeyDown}
      onSubmit={handleSubmit}
      ref={formRef}
      style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
    >
      {submitMessage ? (
        <div
          aria-live='assertive'
          role='alert'
          style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
        >
          {submitMessage}
        </div>
      ) : null}
      <div className='form__field input-container relative' style={{ flex: '1.45 1 0' }}>
        <label className='block' htmlFor='hotel-destination' style={FIELD_LABEL_STYLE}>
          {t('hotels.form.destination')}
        </label>
        <DestinationAutocomplete
          errorId={errors.destination ? 'destination-error' : undefined}
          hasError={Boolean(errors.destination)}
          id='hotel-destination'
          inputRef={(node) => {
            fieldRefs.current.destination = node;
          }}
          onChange={(nextValue) => updateField('destination', nextValue)}
          onSelectDestination={handleSelectDestination}
          value={values.destination}
        />
        {helperRow('destination', 'hotel-destination-helper')}
      </div>

      <div
        className='form__field date-picker package-search__date-picker-wrapper hotel-search__date-picker-wrapper relative'
        style={{ flex: '1.45 1 0', justifyContent: 'flex-start' }}
      >
        <label className='block' style={FIELD_LABEL_STYLE}>
          {t('hotels.form.dateRange')}
        </label>
        <div
          aria-describedby={errors.checkIn || errors.checkOut ? 'checkIn-error' : undefined}
          aria-invalid={errors.checkIn || errors.checkOut ? 'true' : 'false'}
          ref={(node) => {
            fieldRefs.current.checkIn = node;
            fieldRefs.current.checkOut = node;
          }}
          style={{ height: INPUT_HEIGHT }}
          tabIndex={-1}
        >
          <FlightDateRangeSelector
            endDate={parseLocalDate(values.checkOut)}
            endDateLabel={t('hotels.form.checkOut')}
            error={Boolean(errors.checkIn || errors.checkOut)}
            helperText=''
            minDate={new Date()}
            onChange={updateDateRange}
            startDate={parseLocalDate(values.checkIn)}
            startDateLabel={t('hotels.form.checkIn')}
          />
        </div>
        {helperRow(errors.checkIn ? 'checkIn' : 'checkOut', 'hotel-check-in-helper')}
      </div>

      <div className='form__field relative' style={{ flex: '0.85 1 0' }}>
        <label className='block' style={FIELD_LABEL_STYLE}>
          {t('hotels.form.guests')}
        </label>
        <HotelPassengerSelector
          adults={values.adults}
          childrenAges={values.childrenAges}
          childrenCount={values.children}
          describedBy={errors.adults ? 'adults-error' : undefined}
          hasError={Boolean(errors.adults)}
          inputRef={(node) => {
            fieldRefs.current.adults = node;
            fieldRefs.current.children = node;
            fieldRefs.current.rooms = node;
          }}
          onAdultsChange={(adults) => updateGuests({ ...values, adults })}
          onChildrenAgesChange={(childrenAges) => updateGuests({ ...values, childrenAges })}
          onChildrenChange={(children) => updateGuests({ ...values, children })}
          onRoomsChange={(rooms) => updateGuests({ ...values, rooms })}
          rooms={values.rooms}
        />
        {helperRow('adults', 'hotel-guests-helper')}
      </div>

      <div className='package-search__button-row'>
        <button
          aria-label={isSubmitting ? t('hotels.form.loading') : t('hotels.form.submit')}
          className={`package-search__button big-package-search__button${isSubmitting ? ' disabled' : ''}`}
          disabled={isSubmitting}
          onClick={submitCurrentValues}
          style={{
            height: INPUT_HEIGHT,
          }}
          type='button'
        >
          <i aria-hidden='true' className='fa fa-search heading__icon' />
          {isSubmitting ? t('hotels.form.loading') : t('hotels.form.submit')}
        </button>
      </div>
    </form>
  );
}
